/**
 * plasmaPipeline — fire/plasma field with hot colors and audio reactivity.
 *
 * Built from the same volumetric raymarcher foundation as Beat Nebula
 * but with a vertical UV scaling and a fire-color palette. The result
 * looks like a wall of flame or plasma — bright yellow/white at the
 * base, transitioning to orange and red as it rises, with darker
 * violet at the edges.
 *
 * Audio:
 *   - bass: drives the wave amplitude (the "roar" of the fire)
 *   - mid:  drives the noise pattern intensity
 *   - high: drives flicker and ember bursts
 *
 * No mesh, no simulation — single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PlasmaPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + 3.7;
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Centered UV, vertical Y is the "rising" axis.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  // Invert Y so "up" in screen = positive y in shader.
  p.y = -p.y;
  // Slight x compression for narrower fire columns.
  p.x *= 1.5;

  float t = uTime;

  // Audio-driven noise intensity.
  float intensity = 0.7 + 0.5 * bass + 0.3 * mid;

  // Build the flame with domain-warped FBM.
  // We offset the noise field by audio bands for richer texture.
  vec2 noiseUv = p * 2.0;
  noiseUv += vec2(
    fbm(p * 1.5 + vec2(t * 0.3, t * 0.15)) * 1.0 * intensity,
    -t * 0.5  // upward drift
  );

  float n = fbm(noiseUv);

  // Vertical flame shape: brighter at bottom, fading to dark at top.
  float yMask = smoothstep(0.7, -0.2, p.y);  // 1 at bottom, 0 at top
  // The flame goes upward — we want it strongest at the base.
  float flameHeight = 0.4 + 0.3 * bass;
  float flameCore = smoothstep(flameHeight + 0.2, -0.3, p.y);

  // Combine noise with vertical mask.
  float flame = flameCore * (0.7 + 0.5 * n) * intensity;

  // Hot color palette: yellow → orange → red → dark red.
  vec3 yellowHot = vec3(1.0, 0.95, 0.5);
  vec3 orange = vec3(1.0, 0.55, 0.1);
  vec3 red = vec3(0.85, 0.15, 0.05);
  vec3 darkRed = vec3(0.3, 0.05, 0.02);
  vec3 violet = vec3(0.2, 0.02, 0.15);

  // Color shifts with vertical position + noise.
  vec3 col = vec3(0.0);
  col = mix(darkRed, red, smoothstep(0.0, 0.3, flame));
  col = mix(col, orange, smoothstep(0.3, 0.6, flame));
  col = mix(col, yellowHot, smoothstep(0.6, 1.0, flame));

  // Embers rising upward (high audio accent).
  float ember = step(0.96, hash21(floor(gl_FragCoord.xy * vec2(0.4, 0.7)) + floor(t * 8.0)));
  float emberMask = smoothstep(0.5, 0.0, p.y);  // upper half
  col += vec3(1.0, 0.6, 0.2) * ember * emberMask * high * 0.6;

  // Heat shimmer at the bottom (flickering).
  float shimmer = sin(p.x * 12.0 + t * 8.0) * 0.05 + 0.95;
  col *= shimmer * smoothstep(-0.4, -0.1, p.y) * 0.5;

  // Background: deep dark with subtle violet tint.
  vec3 bg = vec3(0.02, 0.01, 0.04);
  // Blend the flame over the bg based on its intensity.
  col = mix(bg, col, clamp(flame * 1.5, 0.0, 1.0));

  // Tone mapping (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computePlasmaBands(t: number, out: Float32Array): void {
  // Strong, rhythmic, like a fire crackling.
  out[0] = 0.4 + 0.4 * Math.abs(Math.sin(t * 2.0));
  out[1] = 0.5 + 0.3 * Math.abs(Math.sin(t * 3.0 + 1.0));
  out[2] = 0.3 + 0.4 * Math.abs(Math.sin(t * 4.0 + 2.0));
  out[3] = 0.4 + 0.4 * Math.abs(Math.sin(t * 2.5 + 0.5));
  out[4] = 0.5 + 0.3 * Math.abs(Math.sin(t * 3.5 + 1.5));
  out[5] = 0.3 + 0.4 * Math.abs(Math.sin(t * 4.5 + 2.5));
  out[6] = 0.3 + 0.5 * Math.abs(Math.sin(t * 6.0 + 0.7));
  out[7] = 0.4 + 0.4 * Math.abs(Math.sin(t * 8.0 + 1.8));
  out[8] = 0.3 + 0.5 * Math.abs(Math.sin(t * 10.0 + 3.0));
}

export function computePlasmaRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030],
    [0.030, 0.080],
    [0.080, 0.150],
    [0.150, 0.260],
    [0.260, 0.380],
    [0.380, 0.500],
    [0.500, 0.660],
    [0.660, 0.820],
    [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPlasmaPipeline(gl: WebGL2RenderingContext): PlasmaPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPlasma(
  gl: WebGL2RenderingContext,
  pipe: PlasmaPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
